import { makeStyles } from "@material-ui/core/styles";

// Horizontal breathing room inside the fieldsets, in theme spacing units.
const FIELDSET_SIDE_PADDING = 5;
// MUI's outlined-input label sits ~8px from the border start.
const NOTCH_OFFSET = 8;

const fieldMarkersEditorStyles = makeStyles((theme) => ({
	// Fieldset drawn like a MUI v4 outlined input (same border color/radius as
	// OutlinedInput's NotchedOutline) so the marker sections read as one form
	// control alongside the sibling fields.
	editor: {
		width: "100%",
		margin: 0,
		border: `1px solid ${
			theme.palette.type === "dark"
				? "rgba(255, 255, 255, 0.23)"
				: "rgba(0, 0, 0, 0.23)"
		}`,
		borderRadius: theme.shape.borderRadius,
		padding: theme.spacing(1, FIELDSET_SIDE_PADDING, 3)
	},
	// Legend sits in the border notch like a shrunk outlined-input label. The
	// legend's horizontal position follows the fieldset's padding-left, so pull
	// it back to the notch offset regardless of the inner-content padding.
	sectionTitle: {
		padding: theme.spacing(0, 0.5),
		marginLeft: NOTCH_OFFSET - theme.spacing(FIELDSET_SIDE_PADDING)
	},
	// Thumb markers render ~48px above the slider track (absolute, bottom-
	// anchored), so the ruler needs headroom or they overlap the content above.
	rulerSpacing: {
		paddingTop: 48
	},
	// Extra bottom room so the yard labels under the track don't crowd the
	// buttons/chips row below.
	ruler: {
		marginBottom: theme.spacing(2)
	},
	rulerWrapper: {
		position: "relative"
	},
	footerRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		flexWrap: "wrap",
		gap: theme.spacing(2)
	},
	// Chips flow horizontally in field order (left→right matches the bands on
	// the ruler) and wrap on narrow screens.
	typeChipList: {
		display: "flex",
		flexWrap: "wrap",
		justifyContent: "flex-end",
		alignItems: "center",
		gap: theme.spacing(2)
	},
	areaBand: {
		position: "absolute",
		top: 10,
		height: 8,
		borderRadius: 2,
		pointerEvents: "none"
	},
	markerWrapper: {
		position: "relative",
		display: "inline-block",
		// Same text color the in-game Yard to Gain marker inherits (see
		// BallPosition.styles ballPosition). Region thumbs override inline
		// with their area color. Fall back to the primary color when the custom
		// menu palette is absent (e.g. the settings pages use the base theme,
		// where theme.palette.menu is undefined — this rule crashed otherwise).
		color:
			theme.palette.menu?.item?.active?.backgroundColor ||
			theme.palette.primary.main
	},
	deleteBadge: {
		position: "absolute",
		top: -6,
		right: -8,
		width: 16,
		height: 16,
		padding: 0,
		border: "none",
		borderRadius: "50%",
		backgroundColor: theme.palette.grey[700],
		color: theme.palette.common.white,
		fontSize: 11,
		lineHeight: "16px",
		textAlign: "center",
		cursor: "pointer",
		pointerEvents: "auto",
		"&:hover": {
			backgroundColor: theme.palette.error.main
		}
	},
	// Clone of BallPosition.styles positionText minus the background-image:
	// the pentagon is drawn by ColoredMarker's inline SVG so the body can be
	// painted with the area color. Text color inherits (theme color from
	// markerWrapper for white-body spots) or is overridden inline to white on
	// painted bodies. Static rules only — thumbs re-render every drag step.
	coloredMarker: {
		position: "relative",
		height: 47,
		width: 38,
		display: "inline-block",
		padding: 2,
		fontFamily: "Barlow Condensed",
		fontWeight: "bold",
		textAlign: "center"
	},
	coloredMarkerIcon: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%"
	},
	coloredMarkerGrid: {
		position: "relative",
		height: "calc(100% - 4px)",
		lineHeight: "normal"
	},
	typeChip: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1)
	},
	typeDot: {
		width: 12,
		height: 12,
		borderRadius: "50%",
		flexShrink: 0
	},
	typeRange: {
		fontSize: 12,
		color: theme.palette.text.secondary,
		whiteSpace: "nowrap"
	}
}));

export default fieldMarkersEditorStyles;
